import React, {useContext} from 'react';
import influencerActions from '../../../../actions/influencer.actions';
import WarningIcon from '@material-ui/icons/Warning';
import {useFormEnableSubmit, useGetMessages} from '../../../../hooks';
import {withTalon} from '../../../../hocs';
import {useFormContext} from 'react-hook-form';
import {CloseAccountContext} from '../..';
import {Button, ConfirmButton} from '../../../../components';
import {PayoutTypes} from '../../../../../shared/types';
import {styled} from '@material-ui/core/styles';
import {Typography} from '@material-ui/core';
import {SubmitHandler} from 'react-hook-form';
import {CloseAccountFormFieldValues} from '../../CloseAccount';
import {useDispatch} from 'react-redux';
import {getLocale} from 'epic-common/src/locale';

const Box = styled('div')(({theme}) => ({
    display: 'flex',
    flexDirection: 'column',
    '& > button, & > a': {
        marginBottom: theme.spacing(4)
    },
    '& > span': {
        marginTop: theme.spacing(1)
    }
}));

const WarningTitle = styled(Typography)(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& > svg': {
        marginRight: theme.spacing(1)
    }
}));

export interface SubmitProps {
    amount?: number;
}

const Submit = withTalon<SubmitProps>(({talon}) => {
    const {isRendering, isLoading, payoutTypeSelected, amount = 0, formattedAmount} = useContext(
        CloseAccountContext
    );

    const _isLoading = isRendering || isLoading || talon.talonState.loading;
    const dispatch = useDispatch();
    const methods = useFormContext<CloseAccountFormFieldValues>();
    const enableSubmitButton = useFormEnableSubmit();
    const messages = useGetMessages(
        {
            cancel: 'epic.affiliate.closeAccount.returnToTerms.v2',
            submit: 'epic.affiliate.closeAccount.submit.v2',
            submitWithPayout: 'epic.affiliate.closeAccount.submit.withPayout.v2',
            warning: 'epic.affiliate.closeAccount.warning.v2',
            warningNoteTitle: 'epic.affiliate.common.finalWarning.v2',
            warningNoteDesc: 'epic.affiliate.closeAccount.warningNote.v2',
            payoutOption: {
                code: 'epic.affiliate.closeAccount.selection.payout.v2',
                args: [<strong key="amount">{formattedAmount}</strong>]
            }
        },
        [amount]
    );

    const submitMessage =
        payoutTypeSelected === PayoutTypes.CASH ? messages.submitWithPayout : messages.submit;
    const showButtonLoaderIcon = methods.formState.isSubmitting || _isLoading;
    const enableButton = !isRendering && !_isLoading && enableSubmitButton;

    const onSubmit: SubmitHandler<CloseAccountFormFieldValues> = data => {
        talon.initCaptcha(solveToken => {
            const {payoutType = 'NONE', disclaimerAccepted = false} = data || {};
            dispatch(
                influencerActions.closeAccount({
                    payoutType,
                    disclaimerAccepted,
                    locale: getLocale(),
                    solveToken
                })
            );
        });
    };

    return (
        <Box>
            <ConfirmButton
                color="error"
                size="large"
                disabled={!enableButton}
                loading={showButtonLoaderIcon}
                isLoading={isRendering}
                label={submitMessage}
                onSubmit={methods.handleSubmit(onSubmit)}
                ConfirmDialogProps={{
                    submit: <span>{submitMessage}</span>,
                    submitButtonProps: {
                        color: 'error',
                        type: 'submit'
                    }
                }}>
                {messages.warning}
                <Box style={{marginTop: 15}}>
                    <WarningTitle color="error" variant="caption">
                        <WarningIcon fontSize="small" />
                        {messages.warningNoteTitle}
                    </WarningTitle>
                    <Typography color="error" variant="caption">
                        {messages.warningNoteDesc}
                    </Typography>
                </Box>
            </ConfirmButton>
            <Button color="secondary" size="large" to="new-terms" isLoading={isRendering}>
                {messages.cancel}
            </Button>
        </Box>
    );
});

export default Submit;
